<div class="split">
  <section class="panel list-panel" aria-labelledby="register-heading">
    <div class="section-heading">
      <div>
        <p class="eyebrow">RECONSTRUCTED FROM THE LOG</p>
        <h2 id="register-heading">Wallet-relying parties</h2>
      </div>
    </div>

    <div class="toolbar">
      <div class="chips" role="group" aria-label="Filter by status">
        @for (filter of filters; track filter.value) {
          <button
            type="button"
            class="chip"
            [class.active]="status() === filter.value"
            [attr.aria-pressed]="status() === filter.value"
            (click)="status.set(filter.value)"
          >
            {{ filter.label }}
            <span class="chip-count">{{ counts()[filter.value] }}</span>
          </button>
        }
      </div>
      <label class="search">
        <mat-icon aria-hidden="true">search</mat-icon>
        <input
          type="search"
          placeholder="Search name or identifier"
          aria-label="Search wallet-relying parties"
          [value]="query()"
          (input)="onQuery($event)"
        />
      </label>
    </div>

    @if (!visible().length) {
      <div class="empty-state">
        <mat-icon>travel_explore</mat-icon>
        <h3>No wallet-relying party matches</h3>
        <p>Registrations and certificates published in the log appear here.</p>
      </div>
    } @else {
      <ul class="rows" role="list">
        @for (row of visible(); track row.wrpId) {
          <li>
            <button
              type="button"
              class="row"
              [class.selected]="selected()?.wrpId === row.wrpId"
              [attr.aria-current]="selected()?.wrpId === row.wrpId"
              (click)="selectedWrpId.set(row.wrpId)"
            >
              <span class="avatar" aria-hidden="true">{{
                row.name.charAt(0)
              }}</span>
              <span class="row-copy"
                ><strong>{{ row.name }}</strong
                ><small class="mono">{{ row.wrpId }}</small></span
              >
              <span class="row-meta">
                <span class="badge" [attr.data-status]="row.status">{{
                  row.status
                }}</span>
                <small
                  >{{ row.access.valid }}/{{ row.access.total }} AC ·
                  {{ row.registration.valid }}/{{
                    row.registration.total
                  }}
                  RC</small
                >
              </span>
              @if (row.high || row.warning) {
                <span
                  class="finding-count"
                  [class.high]="row.high"
                  [attr.aria-label]="row.high + row.warning + ' findings'"
                  >{{ row.high + row.warning }}</span
                >
              }
              <mat-icon aria-hidden="true">chevron_right</mat-icon>
            </button>
          </li>
        }
      </ul>
    }
  </section>

  <aside class="panel detail-panel" aria-live="polite">
    @if (selected(); as row) {
      <p class="eyebrow">WALLET-RELYING PARTY</p>
      <h2>{{ row.name }}</h2>
      <p class="mono detail-sub">{{ row.wrpId }}</p>
      <dl class="facts">
        <dt>Status</dt>
        <dd>
          <span class="badge" [attr.data-status]="row.status">{{
            row.status
          }}</span>
          @if (row.currentRecord?.status && row.currentRecord; as record) {
            from
            <a [href]="eventLink(record.event)" target="_blank" rel="noopener"
              >registration record #{{ record.event.sequence_number }}</a
            >
          } @else {
            from the certificates
          }
        </dd>
        <dt>Access certs</dt>
        <dd>{{ row.access.valid }} unrevoked of {{ row.access.total }}</dd>
        <dt>Registration certs</dt>
        <dd>
          {{ row.registration.valid }} unrevoked of {{ row.registration.total }}
        </dd>
        <dt>Last activity</dt>
        <dd>
          #{{ row.lastEvent.sequence_number }} ·
          {{ row.lastEvent.published_at | date: "medium" }}
        </dd>
        <dt>Register</dt>
        <dd>
          <a [href]="wrpLink(row.wrpId)" target="_blank" rel="noopener"
            >Signed statement at #{{ context().asOfSequence }}</a
          >
        </dd>
      </dl>

      @if (selectedFindings().length) {
        <h3>Findings</h3>
        <ul class="mini-findings" role="list">
          @for (finding of selectedFindings(); track finding.id) {
            <li>
              <button
                type="button"
                class="link-button"
                (click)="openFinding.emit(finding.id)"
              >
                <span
                  class="dot"
                  [class.high]="finding.severity === 'high'"
                  aria-hidden="true"
                ></span>
                {{ finding.rule }} · {{ finding.title }}
              </button>
            </li>
          }
        </ul>
      }

      @if (row.registrationRecords.length) {
        <h3>Registration records</h3>
        <ol class="timeline">
          @for (entry of row.registrationRecords; track entry.event.event_id) {
            <li [class.superseded]="entry !== row.currentRecord">
              <div class="timeline-head">
                <strong>{{
                  entry === row.currentRecord
                    ? "Current registration"
                    : "Registration record"
                }}</strong>
                @if (entry.status) {
                  <span class="badge" [attr.data-status]="entry.status">{{
                    entry.status
                  }}</span>
                } @else {
                  <span class="badge muted-badge">no status</span>
                }
              </div>
              @if (recordName(entry); as name) {
                <small>{{ name }}</small>
              }
              <small class="mono" [title]="entry.fingerprint">{{
                short(entry.fingerprint)
              }}</small>
              <div class="timeline-events">
                <a
                  [href]="eventLink(entry.event)"
                  target="_blank"
                  rel="noopener"
                  >Recorded #{{ entry.event.sequence_number }} ·
                  {{ entry.event.published_at | date: "mediumDate" }}</a
                >
              </div>
              <small
                class="signature"
                [attr.data-signature]="entry.signature"
                >{{ signatureLabels[entry.signature] }}</small
              >
              @if (entry.payload; as payload) {
                <details class="compact">
                  <summary>Decoded record</summary>
                  <pre class="published">{{ json(payload) }}</pre>
                </details>
              }
              @if (entry.parseError) {
                <small class="error-text"
                  >Unreadable: {{ entry.parseError }}</small
                >
              }
            </li>
          }
        </ol>
      }

      <h3>Certificate history</h3>
      @if (!row.certificates.length) {
        <p class="muted small">No certificate was issued to this WRP.</p>
      }
      <ol class="timeline">
        @for (record of row.certificates; track trackCertificate(record)) {
          <li [class.revoked]="record.revoked">
            <div class="timeline-head">
              <strong>{{ label(record) }}</strong>
              <span class="badge" [class.muted-badge]="record.revoked">{{
                record.revoked ? "revoked" : "unrevoked"
              }}</span>
            </div>
            @if (subject(record); as name) {
              <small>{{ name }}</small>
            }
            @if (requests(record); as summary) {
              <small>{{ summary }}</small>
            }
            @if (replacement(record); as note) {
              <small class="replacement">{{ note }}</small>
            }
            <small class="mono" [title]="record.fingerprint">{{
              short(record.fingerprint)
            }}</small>
            <div class="timeline-events">
              <a
                [href]="eventLink(record.issued)"
                target="_blank"
                rel="noopener"
                >Issued #{{ record.issued.sequence_number }} ·
                {{ record.issued.published_at | date: "mediumDate" }}</a
              >
              @if (record.revoked; as revoked) {
                <a [href]="eventLink(revoked)" target="_blank" rel="noopener"
                  >Revoked #{{ revoked.sequence_number }} ·
                  {{
                    revoked.effective_at || revoked.published_at
                      | date: "mediumDate"
                  }}
                  · {{ reason(revoked) }}</a
                >
              }
            </div>
            <small class="signature" [attr.data-signature]="record.signature">{{
              signatureLabels[record.signature]
            }}</small>
            <button
              type="button"
              class="link-button inline details-button"
              (click)="inspect(record)"
            >
              <mat-icon aria-hidden="true">article</mat-icon>Decoded certificate
            </button>
            @if (record.parseError) {
              <small class="error-text"
                >Unreadable: {{ record.parseError }}</small
              >
            }
          </li>
        }
      </ol>
    } @else {
      <div class="empty-state compact">
        <mat-icon>badge</mat-icon>
        <p>Select a wallet-relying party.</p>
      </div>
    }
  </aside>
</div>
